import React from "react";
import {
	Box,
	Chip,
	Divider,
	Drawer,
	List,
	ListItemButton,
	ListItemIcon,
	ListItemText,
	Toolbar,
	Typography,
	useMediaQuery,
	useTheme,
} from "@mui/material";
import DashboardIcon from "@mui/icons-material/Dashboard";
import EventRepeatIcon from "@mui/icons-material/EventRepeat";
import BadgeIcon from "@mui/icons-material/Badge";
import BeachAccessIcon from "@mui/icons-material/BeachAccess";
import AdminPanelSettingsIcon from "@mui/icons-material/AdminPanelSettings";
import PaymentsIcon from "@mui/icons-material/Payments";
import AccessTimeIcon from "@mui/icons-material/AccessTime";
import StoreIcon from "@mui/icons-material/Store";
import NotificationsActiveIcon from "@mui/icons-material/NotificationsActive";
import { useLocation, useNavigate } from "react-router-dom";
import { useAuth } from "../contexts/AuthContext";
import { useBranding } from "../contexts/BrandingContext";

const drawerWidth = 280;

const menuItems = [
	{ label: "Dashboard", path: "/", icon: <DashboardIcon />, roles: ["SUPERADMIN","ADMIN", "MANAGER", "DIPENDENTE"] },
	{ label: "Turni", path: "/turni", icon: <EventRepeatIcon />, roles: ["SUPERADMIN","ADMIN", "MANAGER", "DIPENDENTE"] },
	{ label: "Locali", path: "/locali", icon: <StoreIcon />, roles: ["SUPERADMIN","ADMIN", "MANAGER"] },
	{ label: "Dipendenti", path: "/dipendenti", icon: <BadgeIcon />, roles: ["SUPERADMIN","ADMIN", "MANAGER"] },
	{ label: "Disponibilità", path: "/disponibilita", icon: <AccessTimeIcon />, roles: ["SUPERADMIN","ADMIN", "MANAGER"] },
	{ label: "Ferie", path: "/ferie", icon: <BeachAccessIcon />, roles: ["SUPERADMIN","ADMIN", "MANAGER", "DIPENDENTE"] },
	{ label: "Dispositivi", path: "/dispositivi", icon: <NotificationsActiveIcon />, roles: ["SUPERADMIN","ADMIN", "MANAGER", "DIPENDENTE"] },
	{ label: "Admin", path: "/admin", icon: <AdminPanelSettingsIcon />, roles: ["SUPERADMIN","ADMIN"] },
	{ label: "Pagamenti", path: "/pagamenti", icon: <PaymentsIcon />, roles: ["SUPERADMIN","ADMIN"] },
];

export { menuItems };

export default function Sidebar({ mobileOpen, onClose }) {
	const navigate = useNavigate();
	const location = useLocation();
	const { user, hasAnyRole } = useAuth();
	const { branding } = useBranding();
	const theme = useTheme();
	const isDesktop = useMediaQuery(theme.breakpoints.up("md"));
	const isVisible = branding.layout === "SIDEBAR" && branding.sidebarEnabled !== false;
	if (!isVisible) {
		return null;
	}

	const visibleItems = menuItems.filter((item) => hasAnyRole(item.roles));

	const drawerContent = (
		<>
			<Toolbar>
				<Box>
					<Typography variant="h6" sx={{ fontWeight: 700, letterSpacing: 0.6 }}>
						{branding.headerTitle || "ShiftFlow SaaS"}
					</Typography>
					{branding.logoUrl ? (
						<Box
							component="img"
							src={branding.logoUrl}
							alt={branding.headerTitle || "Logo aziendale"}
							sx={{ mt: 1, maxHeight: 40, maxWidth: 180, objectFit: "contain" }}
						/>
					) : null}
					<Chip label={user?.role || "DIPENDENTE"} size="small" color="primary" sx={{ mt: 1 }} />
				</Box>
			</Toolbar>
			<Divider />
			<List sx={{ px: 1 }}>
				{visibleItems.map((item) => (
					<ListItemButton
						key={item.path}
						selected={location.pathname === item.path}
						onClick={() => {
							navigate(item.path);
							if (!isDesktop) {
								onClose();
							}
						}}
						sx={{ borderRadius: 2, mb: 0.5 }}
					>
						<ListItemIcon>{item.icon}</ListItemIcon>
						<ListItemText primary={item.label} />
					</ListItemButton>
				))}
			</List>
		</>
	);

	return (
		<>
			{isDesktop && (
				<Drawer
					variant="permanent"
					sx={{
						width: drawerWidth,
						flexShrink: 0,
						"& .MuiDrawer-paper": {
							width: drawerWidth,
							boxSizing: "border-box",
							borderRight: "1px solid rgba(255,255,255,0.08)",
								backgroundImage: branding.pageShellGradient || "radial-gradient(circle at 20% 20%, rgba(0,198,255,0.12), transparent 40%), radial-gradient(circle at 80% 10%, rgba(255,153,102,0.14), transparent 35%)",
						},
					}}
					open
				>
					{drawerContent}
				</Drawer>
			)}

			{!isDesktop && (
				<Drawer
					variant="temporary"
					open={mobileOpen}
					onClose={onClose}
					ModalProps={{ keepMounted: true }}
					sx={{
						"& .MuiDrawer-paper": {
							width: drawerWidth,
							boxSizing: "border-box",
						},
					}}
				>
					{drawerContent}
				</Drawer>
			)}
		</>
	);
}
